/**
 * Browser TTS for FC quality_mode=low.
 *
 * Web Speech only (`speechSynthesis`) — Spanish voice picking + softened rate.
 * High / Live use a different audio path and do not go through this service for Eva voice.
 */
import { Injectable } from '@angular/core';

/** Preferred voice name fragments (case-insensitive). Higher = better. */
const PREFERRED_NAME_SCORES: Array<{ needle: string; score: number }> = [
  { needle: 'paulina', score: 100 },
  { needle: 'sabina', score: 95 },
  { needle: 'dalia', score: 94 },
  { needle: 'jorge', score: 90 },
  { needle: 'google español de estados unidos', score: 88 },
  { needle: 'google español de méxico', score: 88 },
  { needle: 'google español', score: 80 },
  { needle: 'microsoft sabina', score: 92 },
  { needle: 'microsoft dalia', score: 91 },
  { needle: 'online', score: 40 },
  { needle: 'neural', score: 35 },
];

const LATAM_LANG_SCORES: Record<string, number> = {
  'es-mx': 50,
  'es-419': 48,
  'es-us': 46,
  'es-co': 44,
  'es-ar': 42,
  'es-cl': 40,
  'es-pe': 38,
  'es-ve': 36,
};

export const FC_BROWSER_TTS_RATE = 1.12;
const VOICES_WAIT_MS = 1500;

export function scoreSpanishVoice(voice: {
  lang?: string;
  name?: string;
  localService?: boolean;
}): number {
  const lang = (voice.lang || '').toLowerCase();
  if (!lang.startsWith('es')) return -1;

  let score = 10;
  if (lang === 'es-es' || lang === 'es') {
    score = 1;
  } else {
    score += LATAM_LANG_SCORES[lang] ?? 20;
  }

  const name = (voice.name || '').toLowerCase();
  for (const { needle, score: s } of PREFERRED_NAME_SCORES) {
    if (name.includes(needle)) {
      score += s;
      break;
    }
  }

  if (voice.localService === false) score += 25;
  return score;
}

@Injectable({ providedIn: 'root' })
export class FcBrowserTtsService {
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private voicesReady: Promise<void> | null = null;
  /**
   * [PERF] pickSpanishVoice() re-scoring the full voice list on every
   * utterance was redundant — the list only changes on a `voiceschanged`
   * event, so cache the picked voice and invalidate solely on that event
   * rather than TTL/every-call. First-call correctness is unaffected:
   * ensureVoicesLoaded() still runs before the first pick either way.
   */
  private cachedVoice: SpeechSynthesisVoice | null = null;
  private cachedVoiceStale = true;

  constructor() {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.addEventListener?.('voiceschanged', () => {
          this.cachedVoiceStale = true;
        });
      } catch {
        /* ignore */
      }
    }
  }

  async speak(text: string): Promise<void> {
    const trimmed = (text || '').trim();
    if (!trimmed) return;

    this.cancel();
    await this.speakWebSpeech(trimmed);
  }

  cancel(): void {
    this.currentUtterance = null;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
  }

  private async speakWebSpeech(text: string): Promise<void> {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.warn('[FC-LOW] speechSynthesis unavailable — skipping TTS');
      return;
    }

    await this.ensureVoicesLoaded();

    return new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'es-419';
      utterance.rate = FC_BROWSER_TTS_RATE;
      utterance.pitch = 1;

      const voice = this.pickSpanishVoice();
      if (voice) {
        try {
          utterance.voice = voice;
          utterance.lang = voice.lang || 'es-419';
        } catch {
          utterance.lang = voice.lang || 'es-419';
        }
      }

      const done = () => {
        if (this.currentUtterance === utterance) {
          this.currentUtterance = null;
        }
        resolve();
      };

      utterance.onend = done;
      utterance.onerror = () => {
        console.warn('[FC-LOW] TTS utterance error');
        done();
      };

      this.currentUtterance = utterance;
      window.speechSynthesis.speak(utterance);
    });
  }

  private ensureVoicesLoaded(): Promise<void> {
    const synth = window.speechSynthesis;
    if (!synth?.getVoices) return Promise.resolve();

    const existing = synth.getVoices();
    if (existing.length > 0) return Promise.resolve();

    if (this.voicesReady) return this.voicesReady;

    this.voicesReady = new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        try {
          synth.removeEventListener?.('voiceschanged', onChanged as any);
        } catch {
          /* ignore */
        }
        resolve();
      };
      const onChanged = () => finish();
      try {
        synth.addEventListener?.('voiceschanged', onChanged as any);
      } catch {
        finish();
        return;
      }
      setTimeout(finish, VOICES_WAIT_MS);
    });

    return this.voicesReady;
  }

  pickSpanishVoice(): SpeechSynthesisVoice | null {
    if (!this.cachedVoiceStale) return this.cachedVoice;
    try {
      const voices = window.speechSynthesis?.getVoices?.() ?? [];
      let best: SpeechSynthesisVoice | null = null;
      let bestScore = -1;
      for (const v of voices) {
        const s = scoreSpanishVoice(v);
        if (s > bestScore) {
          bestScore = s;
          best = v;
        }
      }
      this.cachedVoice = bestScore >= 0 ? best : null;
      this.cachedVoiceStale = false;
      return this.cachedVoice;
    } catch {
      return null;
    }
  }
}
